import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

const PROBLEM_POINTS = [
  {
    title: "You've bought the tools. Adoption is a mystery.",
    body: 'Most companies have invested in Copilot, ChatGPT Enterprise, or similar. But nobody can tell you which employees are actually using them, how effectively, or whether the investment is changing how work gets done.',
  },
  {
    title: 'AI training has become a compliance checkbox.',
    body: "Your L&D team ran an \"AI Fundamentals\" workshop. Completion rate: 73%. Knowledge retained after 30 days: close to zero. The problem isn't that your people don't want to learn — it's that a two-hour seminar can't address the fact that a marketing director and a financial analyst face completely different AI threats on completely different timelines.",
  },
  {
    title: "You don't know who's at risk — or who's ahead.",
    body: "Somewhere in your company, there are people whose roles will look fundamentally different in 18 months. Some are already adapting. Some haven't started. You can't tell who's who, which departments need intervention, or where your actual exposure lies.",
  },
]

const UNDERLYING_ISSUES = [
  'Top performers leaving for companies with structured AI development',
  "Strategic AI initiatives stalling because the workforce isn't ready",
  "Restructuring that's reactive, expensive, and demoralizing",
]

const EMPLOYEE_METRICS = [
  'Overall AI exposure score (0-100) with trend over time',
  'Task-level breakdown — which specific daily tasks are in the path of automation',
  "Adaptability score — how effectively they're already using AI tools",
  'Positioning score — how defensible their unique skills are',
  'Market risk score — how fast AI is moving in their specific function',
]

const COMPANY_METRICS = [
  'Department heatmaps — which teams have the highest aggregate exposure',
  'Distribution curves — how scores are spread across the organization',
  'Progress tracking — which employees are improving, which are stalling',
  'Skill gap analysis — where your biggest capability deficits lie',
  'Benchmark data — how your workforce compares to industry peers',
]

const DEVELOPMENT_FEATURES = [
  {
    title: 'Personal AI Coach for Every Employee',
    body: "Each employee gets a coach that knows their role, their tasks, their score, and your company's priorities. Not a chatbot with generic prompts — a strategist that gives them specific, daily actions to become more effective with AI.",
    bullets: [
      "Daily coaching tailored to each employee's exact role and task profile",
      'Skill-leveled to their career level — not too basic, not too advanced',
      "Advice that accounts for your company's industry, size, and AI maturity",
      'Progress visible to both the employee and their manager',
    ],
  },
  {
    title: 'Daily Intelligence — Not Monthly Training',
    body: 'Every morning, each employee sees the AI developments that affect their specific work. A financial analyst sees different news than a product manager. The intelligence is filtered, contextualized, and paired with an action they can take that day.',
    bullets: [
      'Hundreds of AI developments scanned daily, filtered per role',
      "Contextualized for the employee's seniority and company context",
      'Paired with a specific coaching action — not just "stay informed"',
      'Builds AI literacy cumulatively, 5 minutes a day, over months',
    ],
  },
  {
    title: 'Inject Your Strategic Goals',
    body: "Your company has specific AI priorities — maybe you want engineering to adopt Copilot, or you want sales to integrate AI prospecting. Reposit lets you inject those goals directly into each employee's coaching system.",
    bullets: [
      'Set company-wide or department-specific AI adoption goals',
      'Coaching automatically aligns individual development with your strategy',
      'Track progress on strategic goals at the team and individual level',
      'Adjust priorities as your strategy evolves — the coaching adapts in real time',
    ],
  },
  {
    title: 'Track Progress Over Time',
    body: 'Watch individual scores improve week over week. See which departments are accelerating and which are stuck. Know whether your AI transformation is actually working — with real numbers, not completion certificates.',
    bullets: [
      'Individual employee score trajectories over time',
      'Department and team-level trend analysis',
      'Identify high-performers who can become internal AI champions',
      'Spot employees who need additional support before they disengage',
    ],
  },
]

const CONFIG_CARDS = [
  {
    title: 'Custom Strategic Goals',
    body: "Define what AI readiness means for your organization. Set adoption targets by department, prioritize specific tools or workflows, and let the system align every employee's development accordingly.",
    bullets: [
      'Company-specific AI adoption OKRs',
      'Department-level priority setting',
      'Tool-specific adoption tracking',
    ],
  },
  {
    title: 'Industry Context',
    body: "Coaching is calibrated to your industry. A healthcare company's AI exposure landscape is fundamentally different from a financial services firm's.",
    bullets: [
      'Industry-specific AI development scanning',
      'Competitive landscape intelligence',
      'Regulatory awareness in coaching',
    ],
  },
  {
    title: 'Organizational Structure',
    body: "Map your departments, teams, and reporting lines. Managers see their direct reports' progress. VPs see department views. The C-suite sees the whole organization.",
    bullets: [
      'Role-based access and reporting',
      'Manager dashboards with team insights',
      'Executive-level aggregate views',
    ],
  },
]

const STEPS = [
  {
    num: '01',
    title: 'Configure',
    body: 'We set up the platform for your organization — your structure, your strategic AI goals, your industry context. Takes one call.',
  },
  {
    num: '02',
    title: 'Assess',
    body: 'Each employee completes a 3-minute assessment. No preparation needed. They answer questions about their role, tasks, and current AI usage. They get their personal score immediately.',
  },
  {
    num: '03',
    title: 'Develop',
    body: 'Every morning, each employee receives personalized intelligence and coaching actions. Their scores update as they develop. Your dashboard shows progress in real time.',
  },
  {
    num: '04',
    title: 'Steer',
    body: 'Leadership injects strategic priorities, identifies internal AI champions, allocates resources to teams that need support, and tracks the transformation with hard data — not opinions.',
  },
]

const PERSONAS = [
  {
    role: 'Chief People Officer',
    question:
      "I need to reskill 2,000 people but I don't know which skills to prioritize for which teams. Our L&D vendor says 'AI literacy' but that's meaningless at the individual level.",
    answer:
      'We show you exactly which skills each department needs, ranked by urgency. Your marketing team needs different AI capabilities than your engineering team. The coaching handles that automatically — each employee gets development specific to their role, not a one-size-fits-all curriculum.',
  },
  {
    role: 'CEO',
    question:
      "We keep saying we're 'AI-first' but I have no idea how many of our people can actually use AI effectively. I can't even tell who's ahead and who's behind.",
    answer:
      "You get a single dashboard with a real-time AI readiness score for every employee, team, and department. You'll know exactly where your organization stands — not based on survey responses, but on actual use analysis.",
  },
  {
    role: 'VP of Engineering',
    question:
      'Half my team uses Copilot daily and the other half refuses to touch it. I need everyone at a baseline — but I also need my senior engineers to be leading AI integration, not just consuming it.',
    answer:
      'Each engineer is scored individually and coached at their level. Junior devs get foundational AI tool adoption. Senior engineers get strategic coaching on leading AI-augmented teams. You can inject company-specific goals and track adoption across the org.',
  },
  {
    role: 'Head of Strategy',
    question:
      "We're planning a major AI initiative but I don't know which parts of the company can absorb it and which will resist. I need to know where the bottlenecks are before we start.",
    answer:
      "Department-level data shows you exactly where readiness is high and where it's low. The daily coaching system starts preparing your people before the initiative launches — so by the time you roll out new tools or processes, the workforce is already moving in that direction.",
  },
  {
    role: 'Chief Learning Officer',
    question:
      "I spent $800K on AI training last year and I can't prove any of it worked. The board wants ROI numbers. All I have is completion rates.",
    answer:
      "You get what completion rates can't: measurable improvement in AI readiness over time. Individual score trajectories, department-level progress, and direct correlation between coaching engagement and capability development.",
  },
  {
    role: 'CFO',
    question:
      "Everyone's asking for AI headcount and AI tools budget but nobody can quantify the ROI. I need to know where AI investment actually moves the needle.",
    answer:
      "We show which teams have the highest exposure and the lowest AI adoption — that's where investment has the biggest impact. We also model the cost of inaction: what reactive restructuring costs versus proactive development.",
  },
]

const ENTERPRISE_FEATURES = [
  'SOC 2 Compliant',
  'SSO / SAML',
  'Unlimited Seats',
  'Dedicated Onboarding',
]

export default function EmployersPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [openPersona, setOpenPersona] = useState<number | null>(null)

  return (
    <div className="bg-app-bg text-primary min-h-screen font-sans">
      {/* Header */}
      <header className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <span className="bg-accent h-6 w-6 rounded-full" />
          <span className="text-primary font-serif text-xl font-bold tracking-tight">
            ACRI
          </span>
        </Link>
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="text-muted hover:text-primary text-sm font-medium no-underline transition-colors"
          >
            {t('pages.employers.header.forIndividuals', {
              defaultValue: 'For Individuals',
            })}
          </Link>
          <button
            onClick={() => navigate('/onboarding')}
            className="bg-accent cursor-pointer rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5"
          >
            {t('pages.employers.header.cta', {
              defaultValue: 'Request a Demo →',
            })}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-6 pt-16 pb-20 text-center md:pt-24 md:pb-28">
        <span className="text-accent text-xs font-semibold tracking-widest uppercase">
          {t('pages.employers.hero.label', {
            defaultValue: 'ACRI for Companies',
          })}
        </span>
        <h1 className="mt-5 font-serif text-[2.25rem] leading-[1.1] font-bold tracking-tight md:text-[3.25rem]">
          {t('pages.employers.hero.titleLine1', {
            defaultValue: 'Score every employee. ',
          })}
          <span className="text-accent italic">
            {t('pages.employers.hero.titleLine2', {
              defaultValue: 'Develop all of them.',
            })}
          </span>
        </h1>
        <p className="text-muted mx-auto mt-6 max-w-2xl text-base leading-relaxed">
          {t('pages.employers.hero.subtitle1', {
            defaultValue:
              'ACRI gives every employee a personal AI exposure score — a precise measure of how much of their daily work is in the path of automation. Then it develops them, daily, into the kind of AI-fluent workforce your company actually needs.',
          })}
        </p>
        <p className="text-muted mx-auto mt-4 max-w-2xl text-base leading-relaxed">
          {t('pages.employers.hero.subtitle2', {
            defaultValue:
              'Not a training program they forget. A system that makes your entire organization measurably better at using AI — on your strategic terms.',
          })}
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            onClick={() => navigate('/onboarding')}
            className="bg-accent cursor-pointer rounded-lg px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            {t('pages.employers.hero.cta', {
              defaultValue: 'Request a Demo →',
            })}
          </button>
          <button
            onClick={() => navigate('/onboarding')}
            className="cursor-pointer rounded-lg bg-[#1a1a1a] px-6 py-3 text-sm font-semibold text-white no-underline transition hover:bg-[#2c2c2c]"
          >
            {t('pages.employers.hero.tryIndividual', {
              defaultValue: 'Try the Individual Assessment',
            })}
          </button>
        </div>
        <p className="text-muted mt-4 text-xs">
          {t('pages.employers.hero.assessmentHint', {
            defaultValue:
              'Take the 3-minute assessment yourself first. See what your employees will experience.',
          })}
        </p>
      </section>

      {/* Problem */}
      <section className="mx-auto max-w-6xl px-6 py-20 md:py-28">
        <h2 className="font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
          {t('pages.employers.problem.titleLine1', {
            defaultValue: 'Every company says they\'re "doing AI." ',
          })}
          <span className="text-accent italic">
            {t('pages.employers.problem.titleLine2', {
              defaultValue: 'Almost none can measure it.',
            })}
          </span>
        </h2>

        <div className="mt-14 grid gap-10 lg:grid-cols-[1.5fr_1fr]">
          <div className="space-y-10">
            {PROBLEM_POINTS.map((point) => (
              <div key={point.title}>
                <h3 className="text-lg font-semibold">{point.title}</h3>
                <p className="text-muted mt-2 text-base leading-relaxed">
                  {point.body}
                </p>
              </div>
            ))}
          </div>

          <div className="self-start rounded-xl bg-[#1a1a1a] p-6">
            <span className="text-accent text-xs font-semibold tracking-widest uppercase">
              The Underlying Issue
            </span>
            <ul className="mt-5 space-y-3">
              {UNDERLYING_ISSUES.map((issue) => (
                <li
                  key={issue}
                  className="flex items-start gap-3 text-sm text-[#ccc]"
                >
                  <span className="bg-accent mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" />
                  {issue}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-[#999]">
              The AI transition isn&apos;t a technology problem. It&apos;s a
              people problem. And people problems require people-level data —
              not company-wide surveys, not department averages, not vendor
              adoption metrics.
            </p>
          </div>
        </div>
      </section>

      {/* The Score */}
      <section className="bg-[#1a1a1a]">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <span className="text-accent text-xs font-semibold tracking-widest uppercase">
            {t('pages.employers.score.label', { defaultValue: 'The Score' })}
          </span>
          <h2 className="mt-4 font-serif text-[1.75rem] font-bold tracking-tight text-white md:text-[2.5rem]">
            {t('pages.employers.score.titleLine1', {
              defaultValue: 'One number per employee. ',
            })}
            <span className="text-accent italic">
              {t('pages.employers.score.titleLine2', {
                defaultValue: 'Everything changes.',
              })}
            </span>
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#999]">
            {t('pages.employers.score.subtitle', {
              defaultValue:
                'Each employee takes a 3-minute assessment. We analyze their specific role, their daily tasks, their AI tool usage, their seniority, and their positioning — then generate a personal AI exposure score from 0 to 100.',
            })}
          </p>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <div className="rounded-xl bg-[#2c2c2c] p-6">
              <span className="text-accent text-xs font-semibold tracking-widest uppercase">
                {t('pages.employers.score.eachEmployeeHeading', {
                  defaultValue: 'What You See for Each Employee',
                })}
              </span>
              <ul className="mt-5 space-y-3">
                {EMPLOYEE_METRICS.map((m) => (
                  <li
                    key={m}
                    className="flex items-start gap-3 text-sm text-[#ccc]"
                  >
                    <span className="bg-accent mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" />
                    {m}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-[#2c2c2c] p-6">
              <span className="text-accent text-xs font-semibold tracking-widest uppercase">
                {t('pages.employers.score.acrossCompanyHeading', {
                  defaultValue: 'What You See Across the Company',
                })}
              </span>
              <ul className="mt-5 space-y-3">
                {COMPANY_METRICS.map((m) => (
                  <li
                    key={m}
                    className="flex items-start gap-3 text-sm text-[#ccc]"
                  >
                    <span className="bg-accent mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" />
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-10 text-sm leading-relaxed text-[#999]">
            {t('pages.employers.score.bottomParagraph', {
              defaultValue:
                "This isn't an annual survey. Scores update continuously as AI capabilities evolve and as employees develop. You're watching a live picture of your organization's AI readiness, not a snapshot from six months ago.",
            })}
          </p>
        </div>
      </section>

      {/* Development */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <span className="text-accent text-xs font-semibold tracking-widest uppercase">
            {t('pages.employers.development.label', {
              defaultValue: 'Beyond Measurement',
            })}
          </span>
          <h2 className="mt-4 font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
            {t('pages.employers.development.titleLine1', {
              defaultValue: 'The score is the beginning. ',
            })}
            <span className="text-accent italic">
              {t('pages.employers.development.titleLine2', {
                defaultValue: 'Development is the product.',
              })}
            </span>
          </h2>
          <p className="text-muted mt-6 max-w-3xl text-base leading-relaxed">
            {t('pages.employers.development.subtitle', {
              defaultValue:
                "Knowing that your marketing team has an average exposure score of 67 is useful. Systematically developing every person on that team — daily, in a way that's specific to their exact tasks and your company's strategic direction — is transformative.",
            })}
          </p>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {DEVELOPMENT_FEATURES.map((feat) => (
              <div
                key={feat.title}
                className="border-border rounded-xl border bg-white p-6"
              >
                <h3 className="text-lg font-semibold">{feat.title}</h3>
                <p className="text-muted mt-2 text-sm leading-relaxed">
                  {feat.body}
                </p>
                <ul className="mt-4 space-y-2">
                  {feat.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-muted flex items-start gap-2 text-sm"
                    >
                      <span className="bg-accent mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="border-border rounded-xl border bg-white p-6">
              <h3 className="text-base font-semibold">
                {t('pages.employers.development.vsTrainingHeading', {
                  defaultValue: 'What Makes This Different from Training',
                })}
              </h3>
              <p className="text-muted mt-2 text-sm leading-relaxed">
                Training is an event. This is a system. Training teaches
                concepts. This changes behavior. Training gives everyone the
                same content. This gives each person what they specifically
                need, based on what they specifically do, updated every day
                based on what&apos;s specifically changing in their field.
              </p>
            </div>
            <div className="border-border rounded-xl border bg-white p-6">
              <h3 className="text-base font-semibold">
                {t('pages.employers.development.whyDailyHeading', {
                  defaultValue: 'Why Daily Matters',
                })}
              </h3>
              <p className="text-muted mt-2 text-sm leading-relaxed">
                AI capabilities don&apos;t change quarterly. They change daily.
                A new model drops and suddenly 30% of your content team&apos;s
                workflow is automatable. Your employees need to know about these
                shifts the day they happen — not in next quarter&apos;s training
                refresh.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Configuration */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <h2 className="font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
            {t('pages.employers.config.titleLine1', {
              defaultValue: 'Configured for your company. ',
            })}
            <span className="text-accent italic">
              {t('pages.employers.config.titleLine2', {
                defaultValue: 'Not a generic platform.',
              })}
            </span>
          </h2>
          <p className="text-muted mt-6 max-w-3xl text-base leading-relaxed">
            {t('pages.employers.config.subtitle', {
              defaultValue:
                "Every company's AI transition is different. A 50-person fintech and a 5,000-person manufacturer have different priorities, different timelines, and different definitions of success. ACRI adapts to yours.",
            })}
          </p>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {CONFIG_CARDS.map((card) => (
              <div
                key={card.title}
                className="border-border rounded-xl border bg-white p-6"
              >
                <h3 className="text-base font-semibold">{card.title}</h3>
                <p className="text-muted mt-2 text-sm leading-relaxed">
                  {card.body}
                </p>
                <ul className="mt-4 space-y-2">
                  {card.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-muted flex items-start gap-2 text-sm"
                    >
                      <span className="bg-accent mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#1a1a1a]">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <h2 className="font-serif text-[1.75rem] font-bold tracking-tight text-white md:text-[2.5rem]">
            {t('pages.employers.howItWorks.titleLine1', {
              defaultValue: 'How it ',
            })}
            <span className="text-accent italic">
              {t('pages.employers.howItWorks.titleLine2', {
                defaultValue: 'works.',
              })}
            </span>
          </h2>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step) => (
              <div
                key={step.num}
                className="relative rounded-xl bg-[#2c2c2c] p-6"
              >
                <span className="absolute top-5 right-5 font-serif text-2xl font-bold text-[#444]">
                  {step.num}
                </span>
                <h3 className="text-lg font-semibold text-white">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#999]">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personas / Questions */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <h2 className="text-center font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
            {t('pages.employers.personas.titleLine1', {
              defaultValue: 'The questions nobody else can ',
            })}
            <span className="text-accent italic">
              {t('pages.employers.personas.titleLine2', {
                defaultValue: 'answer for you.',
              })}
            </span>
          </h2>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {PERSONAS.map((p, idx) => {
              const isOpen = openPersona === idx
              return (
                <div
                  key={p.role}
                  className="border-border flex flex-col rounded-xl border bg-white p-6"
                >
                  <span className="text-accent text-xs font-semibold tracking-widest uppercase">
                    {p.role}
                  </span>
                  <p className="text-muted mt-3 text-sm leading-relaxed italic">
                    &ldquo;{p.question}&rdquo;
                  </p>
                  <button
                    onClick={() => setOpenPersona(isOpen ? null : idx)}
                    className="text-accent mt-4 cursor-pointer self-start text-sm font-semibold hover:underline"
                  >
                    {isOpen
                      ? t('pages.employers.personas.hideAnswer', {
                          defaultValue: 'Hide answer',
                        })
                      : t('pages.employers.personas.seeAnswer', {
                          defaultValue: 'See answer →',
                        })}
                  </button>
                  {isOpen && (
                    <p className="text-muted mt-3 text-sm leading-relaxed">
                      {p.answer}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ROI / Pricing */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
          <div className="text-center">
            <span className="text-accent text-xs font-semibold tracking-widest uppercase">
              {t('pages.employers.roi.label', { defaultValue: 'The Math' })}
            </span>
            <h2 className="mt-4 font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
              {t('pages.employers.roi.titleLine1', {
                defaultValue: 'Develop your workforce — or ',
              })}
              <span className="text-accent italic">
                {t('pages.employers.roi.titleLine2', {
                  defaultValue: 'restructure it.',
                })}
              </span>
            </h2>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <div className="rounded-xl bg-[#1a1a1a] p-8 text-center">
              <p className="text-sm text-[#999]">
                {t('pages.employers.roi.reactiveCostLabel', {
                  defaultValue:
                    'Average cost per employee of reactive restructuring',
                })}
              </p>
              <p className="mt-3 font-serif text-4xl font-bold text-white">
                {t('pages.employers.roi.reactivePrice', {
                  defaultValue: '$125,000',
                })}
              </p>
              <p className="mt-2 text-xs text-[#666]">
                {t('pages.employers.roi.reactiveFootnote', {
                  defaultValue:
                    'severance + recruiting + onboarding + lost productivity',
                })}
              </p>
            </div>
            <div className="border-accent rounded-xl border-2 bg-white p-8 text-center">
              <p className="text-muted text-sm">
                {t('pages.employers.roi.proactiveCostLabel', {
                  defaultValue:
                    'Average cost per employee of proactive development',
                })}
              </p>
              <p className="text-accent mt-3 font-serif text-4xl font-bold">
                {t('pages.employers.roi.proactivePrice', {
                  defaultValue: '$29',
                })}
                <span className="text-muted text-lg font-normal">
                  {t('pages.employers.roi.perMonth', {
                    defaultValue: '/month',
                  })}
                </span>
              </p>
              <p className="text-muted mt-2 text-xs">
                {t('pages.employers.roi.proactiveFootnote', {
                  defaultValue:
                    'daily coaching, intelligence, and progress tracking',
                })}
              </p>
            </div>
          </div>

          <p className="text-muted mt-10 text-center text-base leading-relaxed">
            {t('pages.employers.roi.paragraph1', {
              defaultValue:
                "For a 500-person company where 15% of roles face significant AI disruption in the next 24 months, that's 75 employees. Restructuring those roles reactively costs ~$9.4M. Developing all 500 employees proactively costs $174K/year.",
            })}
          </p>
          <p className="text-muted mt-4 text-center text-base leading-relaxed">
            {t('pages.employers.roi.paragraph2', {
              defaultValue:
                "But the real value isn't cost avoidance. It's that those 500 employees become measurably better at using AI — every one of them, daily, in a way that's aligned with your strategic priorities.",
            })}
          </p>
        </div>
      </section>

      {/* Enterprise Features */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-4xl px-6 pb-20 md:pb-28">
          <h3 className="text-center font-serif text-xl font-bold">
            {t('pages.employers.enterprise.heading', {
              defaultValue: 'Enterprise-ready from day one.',
            })}
          </h3>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6">
            {ENTERPRISE_FEATURES.map((f) => (
              <span
                key={f}
                className="border-border text-primary rounded-lg border bg-white px-5 py-3 text-sm font-medium"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-app-bg">
        <div className="mx-auto max-w-2xl px-6 pt-10 pb-16 text-center">
          <span className="text-accent text-xs font-semibold tracking-widest uppercase">
            {t('pages.employers.bottomCta.label', {
              defaultValue: 'Get Started',
            })}
          </span>
          <h2 className="mt-4 font-serif text-[1.75rem] font-bold tracking-tight md:text-[2.5rem]">
            {t('pages.employers.bottomCta.titleLine1', {
              defaultValue: 'See ACRI for ',
            })}
            <span className="text-accent italic">
              {t('pages.employers.bottomCta.titleLine2', {
                defaultValue: 'your team.',
              })}
            </span>
          </h2>
          <p className="text-muted mx-auto mt-4 max-w-lg text-base leading-relaxed">
            {t('pages.employers.bottomCta.subtitle', {
              defaultValue:
                "15-minute demo. We'll show you the employee scoring, the company dashboard, and the coaching system — customized to your industry.",
            })}
          </p>
          <div className="mt-8">
            <button
              onClick={() => navigate('/onboarding')}
              className="bg-accent cursor-pointer rounded-lg px-8 py-3.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              {t('pages.employers.bottomCta.cta', {
                defaultValue: 'Request a Demo →',
              })}
            </button>
          </div>
        </div>
      </section>

      {/* Footer with landscape */}
      <footer>
        <div className="relative h-72 w-full overflow-hidden md:h-96">
          <img
            src="/images/landscape-hero.png"
            alt="Mountain landscape at golden hour"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="bg-[#1a1a1a] py-8">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6">
            <div className="flex items-center gap-2">
              <span className="bg-accent h-6 w-6 rounded-full" />
              <span className="font-serif text-xl font-bold text-white">
                ACRI
              </span>
            </div>
            <div className="flex items-center gap-x-4 text-sm text-white">
              <Link
                to="/"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.home', { defaultValue: 'Home' })}
              </Link>
              <span className="text-[#555]">&middot;</span>
              <Link
                to="https://app-studio.ai/privacy"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.privacy', { defaultValue: 'Privacy' })}
              </Link>
              <span className="text-[#555]">&middot;</span>
              <Link
                to="/help-center/contact"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.contact', { defaultValue: 'Contact' })}
              </Link>
              <span className="text-[#555]">&middot;</span>
              <Link
                to="/help-center/cancel-subscription"
                className="hover:text-accent no-underline transition-colors"
              >
                {t('footer.cancelSubscription', {
                  defaultValue: 'How to Cancel Subscription',
                })}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
